import React, { useState, useEffect, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { Box, Typography, Snackbar, Alert, Chip } from '@mui/material';
import Controls from './controls/Controls';
import { urlAccountantFinancialStatementInvoices, urlFiles } from '../config';
import { logActionType } from "../services/MyCollections";
import { logActivity } from '../services/ApiServices';
import UploadCorrectionDialog from './UploadCorrectionDialog';

export default function AccountantFinancialStatementInvoicesGrid({ year, token, darkTheme, isSupervisorMode, isSupervisorAccountant, isAccountant, roles, userName, contactId }) {
    const [rowData, setRowData] = useState([]);
    const [loading, setLoading] = useState(true);

    // Snackbar
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    // Dialog korekty
    const [correctionDialogOpen, setCorrectionDialogOpen] = useState(false);
    const [correctionTargetRow, setCorrectionTargetRow] = useState(null);

    const showSnackbar = (message, severity = 'success') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${urlAccountantFinancialStatementInvoices}/year/${year}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setRowData(data);
            } else {
                console.error("Błąd pobierania faktur");
            }
        } catch (error) {
            console.error('Failed to fetch invoices', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [year]);

    const handleDownload = (fileMetadata) => {
        if (!fileMetadata) return;
        fetch(`${urlFiles}/${fileMetadata.id}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`
            }
        })
            .then(response => {
                if (!response.ok) throw new Error('Network response was not ok');
                const contentDisposition = response.headers.get('Content-Disposition');
                let filename = fileMetadata.filename;
                if (contentDisposition && contentDisposition.indexOf('attachment') !== -1) {
                    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                    const matches = filenameRegex.exec(contentDisposition);
                    if (matches != null && matches[1]) {
                        filename = matches[1].replace(/['"]/g, '');
                    }
                }
                return response.blob().then(blob => ({ blob, filename }));
            })
            .then(({ blob, filename }) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
            })
            .catch(error => {
                console.error('Błąd podczas pobierania pliku:', error);
            });
    };

    // Żądanie korekty
    const handleRequestCorrection = async (row) => {
        try {
            const response = await fetch(`${urlAccountantFinancialStatementInvoices}/${row.id}/request-correction`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                showSnackbar(`Wysłano żądanie korekty dla księgowego: ${row.accountantName}`, 'success');
                logActivity(
                    `${logActionType.FINANCIAL_STATEMENT_CORRECTION_REQUEST}księgowy: ${row.accountantName}, rok: ${year}`,
                    userName,
                    token
                );
                loadData(); // Odśwież dane
            } else {
                const errorText = await response.text();
                showSnackbar(`Błąd: ${errorText || 'Nie udało się wysłać żądania korekty.'}`, 'error');
            }
        } catch (error) {
            console.error('Błąd żądania korekty:', error);
            showSnackbar('Błąd sieci podczas wysyłania żądania korekty.', 'error');
        }
    };

    // Upload korekty
    const handleCorrectionUpload = async (file) => {
        if (!correctionTargetRow || !file) return;

        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await fetch(`${urlAccountantFinancialStatementInvoices}/${correctionTargetRow.id}/upload-correction`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (response.ok) {
                showSnackbar('Faktura korygująca została wgrana pomyślnie.', 'success');
                logActivity(
                    `${logActionType.FINANCIAL_STATEMENT_CORRECTION_UPLOAD}księgowy: ${correctionTargetRow.accountantName}, rok: ${year}, plik: ${file.name}`,
                    userName,
                    token
                );
                loadData();
            } else {
                const errorText = await response.text();
                showSnackbar(`Błąd: ${errorText || 'Nie udało się wgrać korekty.'}`, 'error');
            }
        } catch (error) {
            console.error('Błąd uploadu korekty:', error);
            showSnackbar('Błąd sieci podczas wgrywania korekty.', 'error');
        } finally {
            setCorrectionDialogOpen(false);
            setCorrectionTargetRow(null);
        }
    };

    // Czy rola pozwala prosić o korektę
    const canRequestCorrection = roles?.includes('SUPERVISOR') ||
        roles?.includes('ADMIN') ||
        roles?.includes('ADMIN_SUPERVISOR') ||
        roles?.includes('MANAGER') ||
        roles?.includes('ACCOUNT_MANAGER');

    // Czy bieżący użytkownik jest księgowym i czy to jego wiersz
    const canUploadCorrection = (row) => {
        console.log("canUploadCorrection checks:", { isAccountant, rowAccountantId: row.accountantId, contactId, roles });
        if (!isAccountant) return false;
        // Jeśli jest to widok dla konkretnego księgowego, pozwól na wgrywanie tylko dla jego rekordu
        return Number(row.accountantId) === Number(contactId);
    };

    const columnDefs = [
        { headerName: "Księgowy", field: "accountantName", flex: 1 },
        {
            headerName: "Faktura",
            field: "fileMetadata",
            flex: 1,
            cellRenderer: (params) => {
                const meta = params.value;
                if (!meta) return <Typography variant="body2" color="textSecondary">Brak faktury</Typography>;
                return (
                    <Box display="flex" alignItems="center" height="100%">
                        <Controls.PremiumButton
                            variant="glass"
                            label="Pobierz fakturę"
                            icon="fas fa-download"
                            color="primary"
                            onClick={() => handleDownload(meta)}
                            style={{ padding: '4px 12px', minHeight: '30px' }}
                        />
                    </Box>
                );
            }
        },
        {
            headerName: "Korekta",
            field: "correctionFileMetadata",
            flex: 1,
            sortable: false,
            filter: false,
            cellRenderer: (params) => {
                const row = params.data;
                if (!row) return null;

                const correctionMeta = row.correctionFileMetadata;
                const isRequested = row.correctionRequested;

                console.log(`[Korekta cellRenderer] Row ID: ${row.id}, accountantId: ${row.accountantId}, correctionRequested: ${isRequested}, hasMeta: ${!!(correctionMeta && correctionMeta.id)}`);

                // Jeśli korekta wgrana – przycisk pobierania
                if (correctionMeta && correctionMeta.id) {
                    console.log(`[Korekta cellRenderer] Returning Pobierz button for row ${row.id}`);
                    return (
                        <Box display="flex" alignItems="center" height="100%">
                            <Controls.PremiumButton
                                variant="glass"
                                label="Pobierz korektę"
                                icon="fas fa-download"
                                color="primary"
                                onClick={() => handleDownload(correctionMeta)}
                                style={{ padding: '4px 12px', minHeight: '30px' }}
                            />
                        </Box>
                    );
                }

                // Jeśli żądanie korekty aktywne
                if (isRequested) {
                    const canUpload = canUploadCorrection(row);
                    console.log(`[Korekta cellRenderer] Row ${row.id} - isRequested: true, canUpload: ${canUpload} (isAccountant: ${isAccountant}, row.accountantId: ${row.accountantId}, contactId: ${contactId})`);
                    // Księgowa widzi przycisk do wgrania
                    if (canUpload) {
                        return (
                            <Box display="flex" alignItems="center" height="100%">
                                <Controls.PremiumButton
                                    variant="green"
                                    label="Wgraj korektę"
                                    icon="fas fa-upload"
                                    onClick={() => {
                                        setCorrectionTargetRow(row);
                                        setCorrectionDialogOpen(true);
                                    }}
                                    style={{ padding: '4px 12px', minHeight: '30px' }}
                                />
                            </Box>
                        );
                    }

                    // Inne role widzą badge
                    return (
                        <Box display="flex" alignItems="center" height="100%">
                            <Chip
                                label="Oczekuje na korektę"
                                size="small"
                                sx={{
                                    backgroundColor: darkTheme ? '#5c4033' : '#fff3e0',
                                    color: darkTheme ? '#ffb74d' : '#e65100',
                                    fontWeight: 600,
                                    fontSize: '0.75rem'
                                }}
                                icon={<i className="fas fa-clock" style={{ fontSize: '0.7rem', color: darkTheme ? '#ffb74d' : '#e65100' }}></i>}
                            />
                        </Box>
                    );
                }

                // Brak żądania, brak korekty
                console.log(`[Korekta cellRenderer] Returning dash for row ${row.id}`);
                return <Typography variant="body2" color="textSecondary">—</Typography>;
            }
        },
        // Kolumna akcji – przycisk "Poproś o korektę" (tylko dla uprawnionych ról)
        ...(canRequestCorrection ? [{
            headerName: "Akcje",
            field: "actions",
            flex: 1,
            sortable: false,
            filter: false,
            cellRenderer: (params) => {
                const row = params.data;
                if (!row || !row.fileMetadata) return null;

                const hasCorrectionFile = row.correctionFileMetadata && row.correctionFileMetadata.id;
                const isRequested = row.correctionRequested && !hasCorrectionFile;

                let btnLabel = "Poproś o korektę";
                let btnVariant = "green";
                let btnIcon = "fas fa-redo";
                let btnDisabled = false;

                if (hasCorrectionFile) {
                    btnLabel = "Wgrano korektę";
                    btnVariant = "glass";
                    btnIcon = "fas fa-check-double";
                    btnDisabled = true;
                } else if (isRequested) {
                    btnLabel = "Żądanie wysłane";
                    btnVariant = "glass";
                    btnIcon = "fas fa-check";
                    btnDisabled = true;
                }

                return (
                    <Box display="flex" alignItems="center" height="100%">
                        <Controls.PremiumButton
                            variant={btnVariant}
                            label={btnLabel}
                            icon={btnIcon}
                            disabled={btnDisabled}
                            onClick={() => handleRequestCorrection(row)}
                            style={{ padding: '4px 12px', minHeight: '30px' }}
                        />
                    </Box>
                );
            }
        }] : [])
    ];

    return (
        <>
            <Box height="600px" className={darkTheme ? "ag-theme-alpine-dark" : "ag-theme-alpine"}>
                <AgGridReact
                    rowData={rowData}
                    columnDefs={columnDefs}
                    animateRows={true}
                    domLayout="autoHeight"
                    popupParent={document.body}
                />
            </Box>

            <UploadCorrectionDialog
                open={correctionDialogOpen}
                handleClose={() => {
                    setCorrectionDialogOpen(false);
                    setCorrectionTargetRow(null);
                }}
                handleUpload={(file) => handleCorrectionUpload(file)}
                fixedFileType={91}
                title="Wgrywanie korekty do sprawozdania"
                description={`Wybierz plik faktury korygującej do sprawozdania finansowego za rok ${year} (np. PDF, maks. 2 MB).`}
            />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={(_, reason) => { if (reason !== 'clickaway') setSnackbarOpen(false); }}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
}

